import { ethers } from 'ethers';
import { NETWORKS } from '../data/networks';

import { numberToDisplay } from '../tools/util';

const ETHERSCAN_API_KEY = '';
const DEFAULT_CONFIRM_TIMEOUT = 30 * 1000;

const BLOCKCHAINS = {};
const PROVIDERS = {};
NETWORKS.forEach((network) => {
  const { chain_id, http_provider } = network;
  if (chain_id) {
    BLOCKCHAINS[chain_id] = network;
    PROVIDERS[chain_id] = http_provider;
  }
});

export default {
  PROVIDERS,
  getProvider,
  getSymbol,
  getChainName,
  canonicalAddress,
  isSameAddress,
  isValidAddress,
  isValidChain,
  transfer,
  decodeString,
  toWei,
  toEthValue,
  lookupAddress,
  resolve,
  getLogs,
  sortLogs,
  getBlockTimestamp,
  confirmTransaction,
  getLogUrl,
};

const g_providerMap = new Map<number, any>();
export function getProvider(chain_id: number) {
  let provider = g_providerMap.get(chain_id);
  if (!provider) {
    const url = PROVIDERS[chain_id];
    if (url) {
      provider = new ethers.providers.JsonRpcProvider(url);
      g_providerMap.set(chain_id, provider);
    }
  }
  return provider;
}
const g_etherscanMap = new Map<number, any>();
export function getEtherscan(chain_id: number) {
  let provider = g_etherscanMap.get(chain_id);
  if (!provider) {
    try {
      provider = new ethers.providers.EtherscanProvider(
        chain_id,
        ETHERSCAN_API_KEY
      );
      g_etherscanMap.set(chain_id, provider);
    } catch (e) {
      return null;
    }
  }
  return provider;
}
export function isValidChain(chain_id: number) {
  return Boolean(PROVIDERS[chain_id]);
}
export function getSymbol(chain_id: number): string {
  let ret = '';
  const blockchain = BLOCKCHAINS[chain_id];
  if (blockchain && blockchain.symbol) {
    ret = blockchain.symbol;
  }
  return ret;
}
export function getChainName(chain_id: number): string {
  let ret = String(chain_id);
  const blockchain = BLOCKCHAINS[chain_id];
  if (blockchain) {
    ret = blockchain.name || blockchain.symbol;
  }
  return ret;
}
export function canonicalAddress(addr: string): string | undefined {
  let ret;
  try {
    ret = ethers.utils.getAddress(addr.toLowerCase());
  } catch (e) {
    // noop
  }
  return ret;
}
export function isSameAddress(addr1, addr2) {
  let ret = false;
  if (addr1 && addr1 === addr2) {
    ret = true;
  } else if (
    addr1 &&
    addr2 &&
    typeof addr1 === 'string' &&
    typeof addr2 === 'string' &&
    addr1.toLowerCase() === addr2.toLowerCase()
  ) {
    ret = true;
  }
  return ret;
}
export function isValidAddress(addr) {
  let ret = false;
  try {
    if (addr && ethers.utils.isAddress(addr)) {
      ret = true;
    }
  } catch (e) {
    // noop
  }
  return ret;
}
export function transfer(params, done): any {
  const { chain_id, to, value } = params;
  const tx = {
    to,
    value,
    chainId: chain_id,
  };
  done(null, tx);
}
export function decodeString(s: string): string {
  let ret = s;
  try {
    ret = ethers.utils.toUtf8String(s);
  } catch (e) {
    try {
      const array = ethers.utils.arrayify(s);
      ret = array.map((num) => String.fromCharCode(num)).join('');
      ret = ethers.utils.toUtf8String(s);
    } catch (e2) {
      // noop
    }
  }
  return ret;
}
export function toWei(amount: any) {
  let ret;
  try {
    ret = ethers.utils.parseEther(String(amount));
  } catch (e) {
    // noop
  }
  return ret;
}
export function toEthValue(value: any): string {
  let ret = '';
  try {
    ret = ethers.utils.formatEther(value);
    const val = parseFloat(ret);
    if (val && val > 0) {
      ret = numberToDisplay(val);
    }
  } catch (e) {
    //noop
  }
  return ret;
}
export function lookupAddress(address: string, done) {
  try {
    getProvider(1)
      .lookupAddress(address)
      .then(
        (val) => {
          done(null, val);
        },
        (err) => {
          done(err);
        }
      );
  } catch (e) {
    done(e);
  }
}
export function resolve(name: string, done) {
  try {
    getProvider(1)
      .resolveName(name)
      .then(
        (address) => {
          done(null, address);
        },
        (err) => {
          done(err);
        }
      );
  } catch (e) {
    done(e);
  }
}
export function getLogs({ chain_id, address }, done) {
  const provider = getEtherscan(chain_id);
  if (provider) {
    provider.getHistory(address).then(
      (result_list) => {
        if (result_list) {
          result_list.sort(sortLogs);
          result_list.forEach((result) => {
            if (result.to === address) {
              result.type = 'receive';
              ``;
            } else if (result.value && result.value.gt(0)) {
              result.type = 'send';
            } else {
              result.type = 'contract';
            }
          });
        }
        done(null, result_list);
      },
      (err) => {
        done(err);
      }
    );
  } else {
    done('no_provider');
  }
}
export function sortLogs(a, b) {
  if (a.blockNumber === b.blockNumber) {
    if (a.logIndex === undefined) {
      return b.transactionIndex - a.transactionIndex;
    } else {
      return b.logIndex - a.logIndex;
    }
  }
  return b.blockNumber - a.blockNumber;
}
const g_blockTimeMap = {};
export function getBlockTimestamp({ chain_id, block_number }, done) {
  const key = chain_id + '_' + block_number;
  let value = g_blockTimeMap[key];
  if (typeof value === 'number') {
    done(null, value);
  } else if (value) {
    value.push(done);
  } else {
    value = [done];
    g_blockTimeMap[key] = value;
    const provider = getProvider(chain_id);
    provider.getBlock(block_number).then(
      (result) => {
        let err;
        let timestamp;
        if (result) {
          timestamp = result.timestamp;
          g_blockTimeMap[key] = timestamp;
        } else {
          err = 'not_found';
        }
        value.forEach((func) => func(err, timestamp));
      },
      (err) => {
        delete g_blockTimeMap[key];
        value.forEach((func) => func(err));
      }
    );
  }
}
export function confirmTransaction(params, done) {
  const { chain_id, transaction_hash, block_count } = params;
  const provider = getProvider(chain_id);
  const timeout = params.timeout || DEFAULT_CONFIRM_TIMEOUT;
  provider.waitForTransaction(transaction_hash, block_count, timeout).then(
    (receipt) => {
      let err;
      if (!receipt) {
        err = 'not_found';
      } else if (receipt && receipt.status !== 1) {
        err = 'failed';
      }
      done(err);
    },
    (err) => {
      if (err && err.code === 'TIMEOUT') {
        err = 'timeout';
      }
      done(err);
    }
  );
}
function getLogUrl(chain_id: number, log: any) {
  let ret;
  const chain = BLOCKCHAINS[chain_id];
  const { hash } = log;
  if (chain && chain.tx_explorer_url) {
    ret = _fixupUrl(chain.tx_explorer_url, { hash });
  }
  return ret;
}
function _fixupUrl(url: string, params: any): string {
  let ret = url;
  const address = params.address || params.owner;
  if (address) {
    ret = ret.replace('$ADDRESS', address);
  }
  if (params.hash) {
    ret = ret.replace('$HASH', params.hash);
  }
  return ret;
}
